import type { AuditChange } from '@xleddyl/types'

const TRANSLATIONS = 'web.components.organization-modal.adminlog'
const TRUNCATED = '[truncated]'
const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]|$)/
const ENUM_VALUE = /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)+$/

export const adminLogTypeClass = (type?: string | null) => {
   switch (type) {
      case 'CREATE':
         return 'text-primary'
      case 'DELETE':
         return 'text-red'
      case 'UPDATE':
         return 'text-gold'
      default:
         return 'text-grey'
   }
}

export const useAdminLogFormat = () => {
   const { t, locale } = useI18n()
   const { $dayjs } = useNuxtApp()

   const humanize = (value: string) => {
      const spaced = value.replace(/_/g, ' ')
      return spaced.charAt(0).toUpperCase() + spaced.slice(1)
   }

   const truncate = (value: string, max: number) =>
      value.length > max ? `${value.slice(0, max - 1)}…` : value

   const formatValue = (raw: unknown): string => {
      if (raw === null || raw === undefined) return '-'

      if (typeof raw === 'boolean') {
         return raw ? t(`${TRANSLATIONS}.value-yes`) : t(`${TRANSLATIONS}.value-no`)
      }

      if (typeof raw === 'number') return String(raw)

      if (typeof raw === 'string') {
         if (raw === TRUNCATED) return t(`${TRANSLATIONS}.value-too-large`)
         if (!raw.trim()) return '-'
         if (ENUM_VALUE.test(raw)) return t(`enum.${raw}`, {}, { default: raw })

         if (ISO_DATE.test(raw)) {
            const date = $dayjs(raw)
            if (date.isValid()) {
               return date.format(raw.includes('T') ? 'DD MMM YYYY HH:mm' : 'DD MMM YYYY')
            }
         }

         return raw
      }

      if (Array.isArray(raw)) {
         if (!raw.length) return '-'
         return raw.map(formatValue).join(', ')
      }

      if (typeof raw === 'object') {
         const entry = raw as Record<string, unknown>
         const translated = entry[locale.value] ?? entry.it ?? entry.en ?? entry.de
         if (typeof translated === 'string') return translated || '-'
         if (typeof entry.value === 'string') return entry.value
         return JSON.stringify(raw)
      }

      return String(raw)
   }

   const columnLabel = (column: string) =>
      t(`grid.columns.${column}`, {}, { default: humanize(column) })

   return {
      columnLabel,
      actionLabel: (action?: string | null) =>
         action ? t(`${TRANSLATIONS}.action.${action}`, {}, { default: humanize(action) }) : '-',
      tableLabel: (table?: string | null) =>
         table ? t(`${TRANSLATIONS}.table.${table}`, {}, { default: humanize(table) }) : '-',
      typeLabel: (type?: string | null) => (type ? t(`enum.${type}`) : '-'),
      userLabel: (email?: string | null) => email || t(`${TRANSLATIONS}.system-user`),
      value: (raw: unknown, max = 80) => truncate(formatValue(raw), max),
      changesLabels: (changes: AuditChange[]) => changes.map((c) => columnLabel(c.column)),
   }
}
